import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LanguageIcon from "@mui/icons-material/Language";
import { useTranslations, formatTranslation } from "../utils/translation";
import { getLocalizedNoticeContent, getAvailableLanguages } from "../utils/notice-content";
import { SUPPORTED_LANGUAGES } from "../contexts/LanguageContext";
import noticesService, { NoticeDetails, NoticeType } from "../services/notices-service";
import ConsentNoticeForm from "./ConsentNoticeForm";
import { languageService, Language } from "../services/language-service";
import {
  formatEffectiveDateLabel,
  formatNoticeCalendarDate,
  isAssignmentEffective,
} from "../utils/notice-effective-date";

interface NoticeDetailModalProps {
  open: boolean;
  onClose: () => void;
  assignmentId: number;
  /** Client subdomain for policy links in consent notice form */
  clientData?: { subdomain: string };
}

const NoticeDetailModal: React.FC<NoticeDetailModalProps> = ({ open, onClose, assignmentId, clientData }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  // UI translations will use selectedLanguage (will be set after notice loads)
  // Default to English initially, then update when language is selected
  const [uiLanguage, setUiLanguage] = useState<string>("en");
  const t = useTranslations(uiLanguage);

  const [loading, setLoading] = useState<boolean>(true);
  const [acknowledging, setAcknowledging] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [noticeDetails, setNoticeDetails] = useState<NoticeDetails | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("en");
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [allSupportedLanguages, setAllSupportedLanguages] = useState<Language[]>([]);

  useEffect(() => {
    if (open && assignmentId) {
      loadNoticeDetails();
    } else {
      // Reset state when modal closes
      setNoticeDetails(null);
      setError(null);
      setSuccess(null);
      setLoading(true);
      setSelectedLanguage("en");
      setUiLanguage("en");
      setAvailableLanguages([]);
    }
  }, [open, assignmentId]);

  const loadNoticeDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const details = await noticesService.getNoticeDetails(assignmentId);
      setNoticeDetails(details);

      // Extract available languages from content
      if (details.content && details.sourceType === "NOTICE") {
        const available = getAvailableLanguages(details.content);
        setAvailableLanguages(available);

        // Set default language: prefer English, otherwise first available
        let defaultLang = "en";
        if (available.includes("en")) {
          defaultLang = "en";
        } else if (available.length > 0) {
          defaultLang = available[0];
        }
        setSelectedLanguage(defaultLang);
        setUiLanguage(defaultLang); // Also set UI language
      } else if (details.sourceType === "CONSENT_NOTICE") {
        // For Consent Notices, fetch all supported languages from API
        try {
          const allLanguages = await languageService.getSupportedLanguages();
          const allLangCodes = allLanguages.map((lang) => lang.code);
          setAllSupportedLanguages(allLanguages);
          setAvailableLanguages(allLangCodes);
          setSelectedLanguage("en");
          setUiLanguage("en");
        } catch (err) {
          console.warn("NoticeDetailModal - Failed to fetch all languages for consent notice, using fallback:", err);
          // Fallback to English if API fails
          setAllSupportedLanguages([languageService.getDefaultLanguage()]);
          setAvailableLanguages(["en"]);
          setSelectedLanguage("en");
          setUiLanguage("en");
        }
      }
    } catch (err: any) {
      console.error("Error loading notice details:", err);
      setError(err?.message || "Failed to load notice details");
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!noticeDetails || noticeDetails.isAcknowledged) return;

    try {
      setAcknowledging(true);
      setError(null);
      setSuccess(null);

      await noticesService.acknowledgeNotice(assignmentId, selectedLanguage);

      setSuccess(t("noticeAcknowledged"));
      // Reload details to get updated acknowledgment status
      await loadNoticeDetails();

      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error("Error acknowledging notice:", err);
      setError(err?.message || "Failed to acknowledge notice");
    } finally {
      setAcknowledging(false);
    }
  };

  const formatDateTime = (value: string | null | undefined): string => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatNoticeDate = (value: string | null | undefined) =>
    formatNoticeCalendarDate(value, uiLanguage, "long");

  const getNoticeTypeLabel = (): string => {
    if (!noticeDetails) return "";
    if (noticeDetails.sourceType === "CONSENT_NOTICE") {
      return t("consentNotices");
    }
    switch (noticeDetails.noticeType) {
      case "BREACH_NOTICE":
        return t("breachNotices");
      case "ERASURE_NOTICE":
        return t("erasureNotices");
      case "INFORMATIONAL_NOTICE":
        return t("informationalNotices");
      default:
        return t("notice");
    }
  };

  const getNoticeTypeColor = (): "default" | "primary" | "success" | "warning" | "error" | "info" => {
    if (!noticeDetails) return "default";
    if (noticeDetails.sourceType === "CONSENT_NOTICE") {
      return "primary";
    }
    switch (noticeDetails.noticeType) {
      case "BREACH_NOTICE":
        return "error";
      case "ERASURE_NOTICE":
        return "warning";
      case "INFORMATIONAL_NOTICE":
        return "info";
      default:
        return "default";
    }
  };

  // Extract localized content using selected language
  const localizedContent = useMemo(() => {
    if (!noticeDetails?.content) return null;

    if (noticeDetails.sourceType === "NOTICE") {
      // Other Notice - extract from ContentJSONSnapshot using selected language
      return getLocalizedNoticeContent(noticeDetails.content, selectedLanguage);
    } else {
      // Consent Notice - content is already SnapshotJSON (handled differently)
      return noticeDetails.content;
    }
  }, [noticeDetails, selectedLanguage]);

  const isEffective = useMemo(() => {
    if (!noticeDetails?.effectiveDate) return true;
    return isAssignmentEffective(noticeDetails.effectiveDate);
  }, [noticeDetails?.effectiveDate]);

  const notYetEffectiveMessage = useMemo(() => {
    if (!noticeDetails?.effectiveDate || isEffective) return null;
    const dateLabel = formatEffectiveDateLabel(noticeDetails.effectiveDate);
    return formatTranslation("noticeNotYetEffective", uiLanguage, { date: dateLabel });
  }, [noticeDetails?.effectiveDate, isEffective, uiLanguage]);

  const guardianBannerText = useMemo(() => {
    if (!noticeDetails?.isMinor) return null;
    if (noticeDetails.minorDisplayName) {
      return formatTranslation("guardianMinorNoticeBlurbWithName", uiLanguage, {
        name: noticeDetails.minorDisplayName,
      });
    }
    return formatTranslation("guardianMinorNoticeBlurbGeneric", uiLanguage);
  }, [noticeDetails?.isMinor, noticeDetails?.minorDisplayName, uiLanguage]);

  const getLanguageName = (code: string): string => {
    // First try to find in allSupportedLanguages (from API, for consent notices)
    if (allSupportedLanguages.length > 0) {
      const lang = allSupportedLanguages.find((l) => l.code === code);
      if (lang) {
        return lang.localeName || lang.name || code.toUpperCase();
      }
    }
    // Fallback to SUPPORTED_LANGUAGES (hardcoded)
    const lang = SUPPORTED_LANGUAGES.find((l) => l.code === code);
    return lang?.nativeName || lang?.name || code.toUpperCase();
  };

  const handleLanguageChange = (event: any) => {
    const newLang = event.target.value;
    setSelectedLanguage(newLang);
    setUiLanguage(newLang); // Update UI language too
  };

  const renderOtherNoticeContent = () => {
    if (!localizedContent || !noticeDetails) return null;

    const noticeType = noticeDetails.noticeType;

    return (
      <Stack spacing={3}>
        {/* Title */}
        {localizedContent.title && (
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {localizedContent.title}
          </Typography>
        )}

        {/* Informational Notice */}
        {noticeType === "INFORMATIONAL_NOTICE" && (
          <>
            {localizedContent.lawfulGround && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t("lawfulGround")}:
                </Typography>
                <Typography variant="body1">{localizedContent.lawfulGround}</Typography>
              </Box>
            )}
            {localizedContent.information && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t("information")}:
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  {localizedContent.information}
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Breach Notice */}
        {noticeType === "BREACH_NOTICE" && (
          <>
            {localizedContent.affectedData && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t("affectedData")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {localizedContent.affectedData}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}
            {localizedContent.consequences && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t("consequences")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {localizedContent.consequences}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}
            {localizedContent.measuresTaken && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t("measuresTaken")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {localizedContent.measuresTaken}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}
            {localizedContent.recommendedPrecautions && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t("recommendedPrecautions")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {localizedContent.recommendedPrecautions}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}
            {localizedContent.contactInfo && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t("contactInfo")}:
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  {localizedContent.contactInfo}
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Erasure Notice */}
        {noticeType === "ERASURE_NOTICE" && (
          <>
            {localizedContent.dataToBeErased && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t("dataToBeErased")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {localizedContent.dataToBeErased}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}
            {localizedContent.reason && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t("reason")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {localizedContent.reason}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}
            {localizedContent.retentionPeriod && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t("retentionPeriod")}:
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  {localizedContent.retentionPeriod}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Stack>
    );
  };

  const renderConsentNoticeContent = () => {
    console.log("NoticeDetailModal - renderConsentNoticeContent called", {
      loading,
      noticeDetails,
      collectionPointId: noticeDetails?.collectionPointId,
    });

    // For Consent Notices, show "Consent recorded" when already consented, otherwise the form
    if (loading) {
      console.log("NoticeDetailModal - Still loading notice details");
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!noticeDetails) {
      console.warn("NoticeDetailModal - noticeDetails is null");
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">Notice details not available</Typography>
        </Alert>
      );
    }

    // Already consented: show read-only "Consent recorded" view
    if (noticeDetails.isAcknowledged) {
      return (
        <Paper elevation={0} sx={{ p: 3, bgcolor: "success.light", border: "1px solid", borderColor: "success.main", borderRadius: 2 }}>
          <Stack spacing={2} alignItems="flex-start">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckCircleIcon color="success" fontSize="medium" />
              <Typography variant="h6" sx={{ fontWeight: 600, color: "success.dark" }}>
                {t("consentRecorded") || "Consent recorded"}
              </Typography>
            </Box>
            {noticeDetails.name && (
              <Typography variant="body1" color="text.secondary">
                {noticeDetails.name}
              </Typography>
            )}
            {noticeDetails.acknowledgedDate && (
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(noticeDetails.acknowledgedDate)}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {t("consentRecordedDescription") || "You have already given consent for this notice. You can view your consent history under Forms or Purposes."}
            </Typography>
          </Stack>
        </Paper>
      );
    }

    if (!noticeDetails.collectionPointId || noticeDetails.collectionPointId <= 0) {
      console.error("NoticeDetailModal - Invalid collectionPointId:", noticeDetails.collectionPointId);
      console.error("NoticeDetailModal - Full noticeDetails:", noticeDetails);
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">Collection point ID not available</Typography>
        </Alert>
      );
    }

    console.log("NoticeDetailModal - Rendering ConsentNoticeForm with collectionPointId:", noticeDetails.collectionPointId);
    return (
      <ConsentNoticeForm
        collectionPointId={noticeDetails.collectionPointId}
        formDisabled={!isEffective}
        formDisabledMessage={notYetEffectiveMessage ?? undefined}
        onSuccess={() => {
          setSuccess("Consent submitted successfully");
          // Optionally reload notice details or close modal
          setTimeout(() => {
            onClose();
          }, 2000);
        }}
        onError={(error) => {
          console.error("NoticeDetailModal - ConsentNoticeForm error:", error);
          setError(error);
        }}
        onLanguagesDetected={(languages) => {
          console.log("NoticeDetailModal - Languages detected from ConsentNoticeForm:", languages);
          setAvailableLanguages(languages);
          // Set default language if not already set
          if (languages.length > 0 && !languages.includes(selectedLanguage)) {
            const defaultLang = languages.includes("en") ? "en" : languages[0];
            setSelectedLanguage(defaultLang);
            setUiLanguage(defaultLang);
          }
        }}
        externalLanguageCode={selectedLanguage}
        subdomain={clientData?.subdomain}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      PaperProps={{
        sx: {
          maxHeight: fullScreen ? "100vh" : "90vh",
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t("noticeDetails")}
            </Typography>
            {noticeDetails && <Chip label={getNoticeTypeLabel()} color={getNoticeTypeColor()} size="small" />}
          </Box>

          {/* Language Picker - Show for both Other Notices and Consent Notices with multiple languages */}
          {noticeDetails && availableLanguages.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedLanguage}
                onChange={handleLanguageChange}
                sx={{
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  },
                }}
                renderValue={(value) => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LanguageIcon fontSize="small" />
                    <Typography variant="body2">{getLanguageName(value)}</Typography>
                  </Box>
                )}
              >
                {availableLanguages.map((langCode) => {
                  // First try to find in allSupportedLanguages (from API, for consent notices)
                  let lang = allSupportedLanguages.find((l) => l.code === langCode);
                  if (!lang) {
                    // Fallback to SUPPORTED_LANGUAGES (hardcoded)
                    const fallbackLang = SUPPORTED_LANGUAGES.find((l) => l.code === langCode);
                    if (fallbackLang) {
                      lang = { code: fallbackLang.code, name: fallbackLang.name, localeName: fallbackLang.nativeName, translationProvider: null };
                    }
                  }
                  const displayName = lang ? lang.localeName || lang.name || langCode.toUpperCase() : langCode.toUpperCase();
                  const englishName = lang?.name;
                  return (
                    <MenuItem key={langCode} value={langCode}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {displayName}
                        </Typography>
                        {englishName && englishName !== displayName && (
                          <Typography variant="body2" sx={{ color: "text.secondary", ml: "auto" }}>
                            {englishName}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}

          <Button onClick={onClose} size="small" sx={{ minWidth: "auto", p: 1 }} aria-label="close">
            <CloseIcon />
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !noticeDetails ? (
          <Alert severity="warning">{t("notFound")}</Alert>
        ) : (
          <Stack spacing={3}>
            {success && <Alert severity="success">{success}</Alert>}
            {guardianBannerText && <Alert severity="info">{guardianBannerText}</Alert>}

            {/* Metadata */}
            <Box>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("sentOn")}:
                  </Typography>
                  <Typography variant="body2">{formatNoticeDate(noticeDetails.assignedDate)}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("appliesFrom")}:
                  </Typography>
                  <Typography variant="body2">{formatNoticeDate(noticeDetails.effectiveDate)}</Typography>
                </Box>
                {!isEffective && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {notYetEffectiveMessage}
                  </Alert>
                )}
                {noticeDetails.isAcknowledged && noticeDetails.acknowledgedDate && noticeDetails.sourceType !== "CONSENT_NOTICE" && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("acknowledgedDate")}:
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
                      {formatDateTime(noticeDetails.acknowledgedDate)}
                    </Typography>
                  </Box>
                )}
                {/* Acknowledgment badge - only show for non-consent notices */}
                {noticeDetails.sourceType !== "CONSENT_NOTICE" && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("noticeType")}:
                    </Typography>
                    <Chip
                      label={noticeDetails.isAcknowledged ? t("acknowledged") : t("unacknowledged")}
                      color={noticeDetails.isAcknowledged ? "success" : "error"}
                      size="small"
                      icon={noticeDetails.isAcknowledged ? <CheckCircleIcon /> : <WarningAmberIcon />}
                    />
                  </Box>
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Notice Content */}
            {noticeDetails.sourceType === "CONSENT_NOTICE" ? renderConsentNoticeContent() : renderOtherNoticeContent()}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {t("close")}
        </Button>
        {noticeDetails && !noticeDetails.isAcknowledged && noticeDetails.sourceType === "NOTICE" && (
          <Button
            onClick={handleAcknowledge}
            variant="contained"
            color="primary"
            disabled={acknowledging || !isEffective}
            startIcon={acknowledging ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {acknowledging ? t("loading") : t("acknowledge")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NoticeDetailModal;
